import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import dayjs from '~/timeInit';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LoadingButton } from '@mui/lab';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  ToggleButton,
  Tooltip,
  Typography,
} from '@mui/material';

import UserSearchWidget from '~/components/Common/UserSearchWidget';
import { useRegionOptions } from '~/components/Tags/hooks/useRegionOptions';
import {
  buildSuggestedClonedVoucherName,
  filterVoucherRewardsForRegion,
  summarizeVoucherRewardsForRegion,
} from '~/components/Vouchers/voucherCloneUtils';
import type { VoucherRegionRewardSummary } from '~/components/Vouchers/voucherCloneUtils';
import {
  CLONE_VOUCHER_NAME_PATTERN_MESSAGE,
  validateRequiredCloneVoucherName,
} from '~/components/Vouchers/voucherNameValidation';
import { DEFAULT_VOUCHER_USER_EXPIRY_YEARS } from '~/components/Vouchers/voucherUserConstants';

import { toPromoBrand } from '~/consts/promoBrand';

import { useCloneVoucher } from '~/hooks/apis/promo/useCloneVoucher';
import { useCloneVoucherPreview } from '~/hooks/apis/promo/useCloneVoucherPreview';
import { useCreateVoucherUsers } from '~/hooks/apis/promo/useCreateVoucherUsers';
import { useGetVouchersByIds } from '~/hooks/apis/promo/useGetVouchersByIds';
import { areCloneVoucherPreviewParamsEqual, voucherCloneQueryKeys } from '~/hooks/apis/promo/voucherCloneQueryKeys';
import useCurrentBrand from '~/hooks/useCurrentBrand';

import type {
  CloneVoucherPreviewQuery,
  CloneVoucherRequestBody,
  CloneVoucherUserAssignment,
} from '~/services/PromoService';
import { SYDNEY_TIMEZONE } from '~/services/TimeService';

import ApiError from '~/types/ApiError';

import { normalizeToISODate } from '~/utils/dateFormatter';

/** Voucher IDs from promo are UUIDs; used to choose sourceVoucherId vs sourceVoucherName for the clone API. */
const SOURCE_VOUCHER_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function validateNewVoucherName(value: string): true | string {
  return validateRequiredCloneVoucherName(value);
}

function sourceQueryFromInput(
  sourceVoucherInput: string,
  fallbackSourceVoucherId: string,
): Pick<CloneVoucherPreviewQuery, 'sourceVoucherId' | 'sourceVoucherName'> {
  const raw = sourceVoucherInput.trim();
  if (!raw) {
    return { sourceVoucherId: fallbackSourceVoucherId };
  }
  if (SOURCE_VOUCHER_UUID_RE.test(raw)) {
    return { sourceVoucherId: raw };
  }
  return { sourceVoucherName: raw };
}

/**
 * Voucher UUID to attach users to (the clone source). Matches preview source when possible.
 * Returns null if the field contains another voucher’s name only (unresolved) while editable.
 */
function resolvedVoucherIdForAssignUsers(
  fallbackSourceVoucherId: string,
  sourceVoucherInput: string,
  isSourceLocked: boolean,
): string | null {
  const raw = sourceVoucherInput.trim();
  const fallback = fallbackSourceVoucherId.trim();
  if (SOURCE_VOUCHER_UUID_RE.test(raw)) {
    return raw;
  }
  if (!raw) {
    return fallback || null;
  }
  if (isSourceLocked) {
    return fallback || null;
  }
  return null;
}

type VoucherUserRow = {
  id: string;
  userId: string;
  /** Display string for UserSearchWidget after a selection (e.g. email). */
  userSearchLabel: string;
};

/** When a region is selected: override the reward amount, or copy that region’s discounts without sending an amount. */
type RegionalVoucherCloneMode = 'amountOverride' | 'regionOnly';

/** When the source voucher has a single reward, used to pre-fill regional clone amount after a region is chosen. */
export type VoucherCloneSourceReward = {
  reward_amount: number;
  currency_code?: string;
};

export type VoucherCloneDialogProps = {
  open: boolean;
  onClose: () => void;
  sourceVoucherId: string;
  /** Shown in helper text; optional name of the voucher being edited */
  sourceVoucherDisplayName?: string;
  /**
   * Current source voucher rewards (e.g. from the edit form). Regional hints match promo clone rules:
   * rewards for the selected region’s currency (`currency_code`, or voucher default when omitted).
   */
  sourceVoucherRewards?: ReadonlyArray<VoucherCloneSourceReward>;
  /** Voucher-level currency; used when a reward row omits `currency_code`. */
  sourceVoucherCurrencyCode?: string;
  /**
   * When true (default if `sourceVoucherId` is non-empty), source ID/name is pre-filled and not editable.
   * Set false to allow choosing another source voucher in the field.
   */
  lockSourceVoucher?: boolean;
  /** Pre-filled new name, e.g. `${currentName}-copy` */
  suggestedNewVoucherName?: string;
  /**
   * When this voucher was created by cloning, the source voucher id (`clonedFromId` from the API).
   * If set, the dialog loads that voucher’s name and uses it to build the suggested new name (cleaner than
   * chaining suffixes from the current auto-generated voucher name).
   */
  clonedFromVoucherId?: string | undefined | null;
  onCloned: (newVoucherId: string) => void;
};

function newRow(): VoucherUserRow {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    userId: '',
    userSearchLabel: '',
  };
}

/**
 * Preview GET query: with a region, either region-only (no amount) or regional override (valid amount).
 */
function buildClonePreviewQuery(
  newVoucherName: string,
  sourceVoucherInput: string,
  fallbackSourceVoucherId: string,
  region: string,
  amountStr: string,
  regionalMode: RegionalVoucherCloneMode,
): CloneVoucherPreviewQuery | null {
  const name = newVoucherName.trim();
  if (validateNewVoucherName(name) !== true) {
    return null;
  }
  const base: CloneVoucherPreviewQuery = {
    newVoucherName: name,
    ...sourceQueryFromInput(sourceVoucherInput, fallbackSourceVoucherId),
  };
  const regionTrim = region.trim();
  if (!regionTrim) {
    return base;
  }
  if (regionalMode === 'regionOnly') {
    return { ...base, region: regionTrim };
  }
  const trimmedAmount = amountStr.trim();
  if (trimmedAmount === '') {
    return null;
  }
  const amount = Number(trimmedAmount);
  if (!Number.isFinite(amount) || amount < 0) {
    return null;
  }
  return { ...base, region: regionTrim, amount };
}

/** Strict base for clone POST — regional override requires a valid amount; region-only sends region only. */
function buildVoucherCloneBaseFields(
  newVoucherName: string,
  sourceVoucherInput: string,
  fallbackSourceVoucherId: string,
  region: string,
  amountStr: string,
  regionalMode: RegionalVoucherCloneMode,
): CloneVoucherPreviewQuery | null {
  const name = newVoucherName.trim();
  if (validateNewVoucherName(name) !== true) {
    return null;
  }
  const base: CloneVoucherPreviewQuery = {
    newVoucherName: name,
    ...sourceQueryFromInput(sourceVoucherInput, fallbackSourceVoucherId),
  };
  const regionTrim = region.trim();
  if (regionTrim) {
    base.region = regionTrim;
    if (regionalMode === 'amountOverride') {
      const amount = Number(amountStr);
      if (!Number.isFinite(amount) || amount < 0) {
        return null;
      }
      base.amount = amount;
    }
  }
  return base;
}

function buildCloneBody(
  newVoucherName: string,
  sourceVoucherInput: string,
  fallbackSourceVoucherId: string,
  region: string,
  amountStr: string,
  regionalMode: RegionalVoucherCloneMode,
  caseId: string,
  reason: string,
  voucherUserRows: Array<VoucherUserRow>,
): CloneVoucherRequestBody | null {
  const base = buildVoucherCloneBaseFields(
    newVoucherName,
    sourceVoucherInput,
    fallbackSourceVoucherId,
    region,
    amountStr,
    regionalMode,
  );
  if (!base) {
    return null;
  }
  const body: CloneVoucherRequestBody = { ...base };
  const trimmedCaseId = caseId.trim();
  const trimmedReason = reason.trim();

  if (trimmedCaseId) {
    body.caseId = trimmedCaseId.toUpperCase();
  }

  if (trimmedReason) {
    body.reason = trimmedReason;
  }

  const assignments: Array<CloneVoucherUserAssignment> = [];
  for (const row of voucherUserRows) {
    const userId = row.userId.trim();
    if (!userId) {
      continue;
    }
    const entry: CloneVoucherUserAssignment = { user_id: userId };
    assignments.push(entry);
  }

  if (assignments.length > 0) {
    body.voucherUsers = assignments;
  }

  return body;
}

function validationMessageForMissingBaseFields(regionFilled: boolean, regionalMode: RegionalVoucherCloneMode): string {
  if (!regionFilled) {
    return 'Enter a new voucher name.';
  }
  return regionalMode === 'amountOverride'
    ? 'Enter a valid reward amount (0 or greater) for the selected region.'
    : 'Enter a new voucher name.';
}

function formatClonePreviewApiErrorDetails(errors: unknown): string {
  if (errors == null || errors === '') {
    return '';
  }
  if (typeof errors === 'string') {
    return errors.trim();
  }
  if (Array.isArray(errors)) {
    return errors
      .map((item) => {
        if (typeof item === 'string') {
          return item.trim();
        }
        if (item && typeof item === 'object' && 'message' in item) {
          const message = (item as { message: unknown }).message;
          return typeof message === 'string' ? message.trim() : '';
        }
        return '';
      })
      .filter(Boolean)
      .join('\n');
  }
  return '';
}

/** Maps thrown `request()` / `ApiError` failures from the clone preview GET into copy for the dialog. */
function formatClonePreviewErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const headline = error.message?.trim() || 'Preview request failed';
    const statusNote = typeof error.status === 'number' ? ` (HTTP ${error.status})` : '';
    const details = formatClonePreviewApiErrorDetails(error.errors);
    return details ? `${headline}${statusNote}\n${details}` : `${headline}${statusNote}`;
  }
  if (error instanceof Error) {
    return error.message.trim() || 'Preview request failed';
  }
  return 'Preview request failed';
}

/** Single-reward regions show an amount hint; multiple rewards show “multiple”; unconfigured regions use an empty suffix. */
function regionRewardSelectPresentation(summary: VoucherRegionRewardSummary): {
  suffix: string;
} {
  if (summary.kind === 'none') {
    return { suffix: '' };
  }
  if (summary.kind === 'multiple') {
    return { suffix: ' — multiple amounts' };
  }
  return {
    suffix: summary.amountLabel ? ` — ${summary.amountLabel}` : '',
  };
}

export default function VoucherCloneDialog({
  open,
  onClose,
  sourceVoucherId,
  sourceVoucherDisplayName,
  lockSourceVoucher: lockSourceVoucherProp,
  suggestedNewVoucherName = '',
  clonedFromVoucherId,
  sourceVoucherRewards,
  sourceVoucherCurrencyCode,
  onCloned,
}: VoucherCloneDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const brand = useCurrentBrand();
  const promoBrand = toPromoBrand(brand);
  const cloneMutation = useCloneVoucher();
  const assignUsersMutation = useCreateVoucherUsers();
  const { regionOptions } = useRegionOptions();

  const validRegionCodes = useMemo(() => new Set(regionOptions.map((opt) => opt.code)), [regionOptions]);

  const isSourceLocked = lockSourceVoucherProp ?? sourceVoucherId.trim().length > 0;

  const [newVoucherName, setNewVoucherName] = useState('');
  const [sourceVoucherInput, setSourceVoucherInput] = useState('');
  const [region, setRegion] = useState('');
  const [regionalCloneMode, setRegionalCloneMode] = useState<RegionalVoucherCloneMode>('regionOnly');
  const [amount, setAmount] = useState('');
  const [caseId, setCaseId] = useState('');
  const [reason, setReason] = useState('');
  const [voucherUserRows, setVoucherUserRows] = useState<Array<VoucherUserRow>>([]);
  const [moreInfo, setMoreInfo] = useState(false);
  const [previewParams, setPreviewParams] = useState<CloneVoucherPreviewQuery | null>(null);
  const wasOpenRef = useRef(false);
  /** Value of "New voucher name" last set by reset (or parent-name upgrade); used to avoid overwriting user edits. */
  const newNameBaselineRef = useRef('');

  const clonedFromIdTrimmed = clonedFromVoucherId?.trim() ?? '';
  const parentVoucherQuery = useGetVouchersByIds({
    voucherIds: clonedFromIdTrimmed ? [clonedFromIdTrimmed] : [],
    brand: brand ?? undefined,
    enabled: open && Boolean(clonedFromIdTrimmed),
  });
  const parentVoucherName = clonedFromIdTrimmed
    ? parentVoucherQuery.data?.get(clonedFromIdTrimmed)?.voucherName?.trim()
    : undefined;

  const hasPerRegionRewardHints = sourceVoucherRewards != null;

  const rewardsForSelectedRegion = useMemo(() => {
    const r = region.trim();
    if (!r || !hasPerRegionRewardHints) {
      return [];
    }
    return filterVoucherRewardsForRegion(sourceVoucherRewards, r, validRegionCodes, sourceVoucherCurrencyCode);
  }, [region, sourceVoucherRewards, validRegionCodes, hasPerRegionRewardHints, sourceVoucherCurrencyCode]);

  const selectedRegionRewardSummary = useMemo(() => {
    if (!hasPerRegionRewardHints || !region.trim()) {
      return null;
    }
    return summarizeVoucherRewardsForRegion(
      sourceVoucherRewards,
      region.trim(),
      validRegionCodes,
      sourceVoucherCurrencyCode,
    );
  }, [hasPerRegionRewardHints, region, sourceVoucherRewards, validRegionCodes, sourceVoucherCurrencyCode]);

  const cannotRegionalAmountOverride = Boolean(
    selectedRegionRewardSummary && selectedRegionRewardSummary.kind === 'multiple',
  );

  useEffect(() => {
    if (!open || !cannotRegionalAmountOverride || regionalCloneMode !== 'amountOverride') {
      return;
    }
    setRegionalCloneMode('regionOnly');
  }, [open, cannotRegionalAmountOverride, regionalCloneMode]);

  /**
   * Pre-fill or clear reward amount when region / rewards change.
   * Avoids leaving a previous region’s amount visible when this region has no matching reward or multiple.
   */
  useEffect(() => {
    if (!open || !region.trim() || regionalCloneMode !== 'regionOnly') {
      return;
    }
    if (!hasPerRegionRewardHints) {
      return;
    }
    const list = rewardsForSelectedRegion;
    if (list.length !== 1) {
      setAmount('');
      return;
    }
    const only = list[0];
    const rewardAmount = Number(only.reward_amount);
    if (!Number.isFinite(rewardAmount)) {
      setAmount('');
      return;
    }
    setAmount(String(rewardAmount));
  }, [open, region, regionalCloneMode, hasPerRegionRewardHints, rewardsForSelectedRegion]);

  useEffect(() => {
    if (!region.trim()) {
      setAmount('');
      setRegionalCloneMode('regionOnly');
    }
  }, [region]);

  useEffect(() => {
    if (regionalCloneMode === 'regionOnly') {
      setAmount('');
    }
  }, [regionalCloneMode]);

  const resetForm = useCallback(() => {
    newNameBaselineRef.current = suggestedNewVoucherName;
    setNewVoucherName(suggestedNewVoucherName);
    if (isSourceLocked) {
      const display = sourceVoucherDisplayName?.trim();
      setSourceVoucherInput(display || sourceVoucherId);
    } else {
      setSourceVoucherInput('');
    }
    setRegion('');
    setRegionalCloneMode('regionOnly');
    setAmount('');
    setCaseId('');
    setReason('');
    setVoucherUserRows([]);
    setMoreInfo(false);
    setPreviewParams(null);
  }, [isSourceLocked, sourceVoucherDisplayName, sourceVoucherId, suggestedNewVoucherName]);

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (justOpened) {
      resetForm();
      queryClient.removeQueries({ queryKey: voucherCloneQueryKeys.previews() });
    }
  }, [open, queryClient, resetForm]);

  /** Prefer the original parent voucher’s name when this voucher is already a clone (avoids stacked random suffixes). */
  useEffect(() => {
    if (!open || !clonedFromIdTrimmed || !parentVoucherName) {
      return;
    }
    const fromParent = buildSuggestedClonedVoucherName(parentVoucherName);
    if (!fromParent) {
      return;
    }
    setNewVoucherName((prev) => {
      if (prev !== newNameBaselineRef.current) {
        return prev;
      }
      newNameBaselineRef.current = fromParent;
      return fromParent;
    });
  }, [open, clonedFromIdTrimmed, parentVoucherName]);

  const previewQuery = useCloneVoucherPreview({
    params: previewParams,
    enabled: open && Boolean(previewParams),
  });

  const renderRegionSelectValue = useCallback(
    (selected: string) => {
      const trimmed = selected.trim();
      if (!trimmed) {
        return (
          <Typography component="span" sx={{ fontStyle: 'italic' }}>
            Clone all regions
          </Typography>
        );
      }
      const opt = regionOptions.find((r) => r.code === trimmed);
      if (!opt) {
        return trimmed;
      }
      if (!hasPerRegionRewardHints) {
        return `${opt.name} (${opt.code})`;
      }
      const summary = summarizeVoucherRewardsForRegion(
        sourceVoucherRewards,
        trimmed,
        validRegionCodes,
        sourceVoucherCurrencyCode,
      );
      const presentation = regionRewardSelectPresentation(summary);
      const isUnavailableRegion = summary.kind === 'none';
      return (
        <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <Typography component="span" variant="inherit" color={isUnavailableRegion ? 'text.disabled' : undefined}>
            {opt.name} ({opt.code})
          </Typography>
          <Typography
            component="span"
            variant="inherit"
            color={isUnavailableRegion ? 'text.disabled' : 'text.secondary'}
          >
            {presentation.suffix}
          </Typography>
        </Box>
      );
    },
    [hasPerRegionRewardHints, regionOptions, sourceVoucherRewards, sourceVoucherCurrencyCode, validRegionCodes],
  );

  const regionFilled = region.trim().length > 0;
  const hasUsersToAssignOnClone = useMemo(
    () => voucherUserRows.some((row) => row.userId.trim().length > 0),
    [voucherUserRows],
  );
  /** No single-region reward override — cloning copies all regions as-is. */
  const showAssignOnVoucherInsteadSuggestion = hasUsersToAssignOnClone && !regionFilled;
  const showRegionalCloneUserAdvice = hasUsersToAssignOnClone && regionFilled;
  const targetVoucherIdForAssign = useMemo(
    () => resolvedVoucherIdForAssignUsers(sourceVoucherId, sourceVoucherInput, isSourceLocked),
    [sourceVoucherId, sourceVoucherInput, isSourceLocked],
  );
  const assignedUserIdsForSourceVoucher = useMemo(
    () => voucherUserRows.map((row) => row.userId.trim()).filter(Boolean),
    [voucherUserRows],
  );
  const canAssignUsersToSourceVoucher =
    showAssignOnVoucherInsteadSuggestion &&
    Boolean(targetVoucherIdForAssign) &&
    assignedUserIdsForSourceVoucher.length > 0;
  const amountValid =
    !regionFilled ||
    regionalCloneMode === 'regionOnly' ||
    (amount.trim() !== '' && Number(amount) >= 0 && Number.isFinite(Number(amount)));
  const newVoucherNameValidation = validateNewVoucherName(newVoucherName);
  const newVoucherNameError =
    newVoucherName.trim() && newVoucherNameValidation !== true ? newVoucherNameValidation : '';
  const canTriggerPreview = newVoucherNameValidation === true;

  const currentPreviewQuery = buildClonePreviewQuery(
    newVoucherName,
    sourceVoucherInput,
    sourceVoucherId,
    region,
    amount,
    regionalCloneMode,
  );
  const currentPreviewQueryWithBrand = currentPreviewQuery
    ? promoBrand
      ? { ...currentPreviewQuery, brand: promoBrand }
      : currentPreviewQuery
    : null;
  const previewMatchesCurrent = Boolean(
    previewParams &&
    currentPreviewQueryWithBrand &&
    areCloneVoucherPreviewParamsEqual(currentPreviewQueryWithBrand, previewParams),
  );
  const previewCompleteForClone =
    !regionFilled ||
    (currentPreviewQuery !== null &&
      (regionalCloneMode === 'regionOnly' || (amountValid && currentPreviewQuery.amount !== undefined)));
  const canCloneAfterPreview =
    canTriggerPreview &&
    previewCompleteForClone &&
    previewMatchesCurrent &&
    previewQuery.isSuccess &&
    Boolean(previewQuery.data?.result) &&
    Boolean(previewQuery.data?.result?.newVoucherNameAvailable);

  /** Drop stale preview as soon as form fields diverge (e.g. region change). */
  useEffect(() => {
    if (!open || !previewParams) {
      return;
    }
    const current = buildClonePreviewQuery(
      newVoucherName,
      sourceVoucherInput,
      sourceVoucherId,
      region,
      amount,
      regionalCloneMode,
    );
    const currentWithBrand = current ? (promoBrand ? { ...current, brand: promoBrand } : current) : null;
    if (currentWithBrand && areCloneVoucherPreviewParamsEqual(currentWithBrand, previewParams)) {
      return;
    }
    setPreviewParams(null);
  }, [
    newVoucherName,
    sourceVoucherInput,
    sourceVoucherId,
    region,
    amount,
    regionalCloneMode,
    previewParams,
    open,
    promoBrand,
  ]);

  /** Re-run preview when name, source, region, or amount change. */
  useEffect(() => {
    if (!open) {
      return;
    }
    const q = buildClonePreviewQuery(
      newVoucherName,
      sourceVoucherInput,
      sourceVoucherId,
      region,
      amount,
      regionalCloneMode,
    );
    const id = window.setTimeout(() => {
      if (!q) {
        setPreviewParams(null);
        return;
      }
      setPreviewParams(promoBrand ? { ...q, brand: promoBrand } : q);
    }, 350);
    return () => window.clearTimeout(id);
  }, [open, newVoucherName, sourceVoucherInput, sourceVoucherId, region, amount, regionalCloneMode, promoBrand]);

  const handlePreview = () => {
    const q = buildClonePreviewQuery(
      newVoucherName,
      sourceVoucherInput,
      sourceVoucherId,
      region,
      amount,
      regionalCloneMode,
    );
    if (!q) {
      if (newVoucherNameValidation !== true) {
        enqueueSnackbar(newVoucherNameValidation, { variant: 'warning' });
      } else if (region.trim()) {
        enqueueSnackbar(validationMessageForMissingBaseFields(true, regionalCloneMode), { variant: 'warning' });
      } else {
        enqueueSnackbar('Unable to build preview request.', { variant: 'warning' });
      }
      return;
    }
    const requestParams = promoBrand ? { ...q, brand: promoBrand } : q;
    const samePreviewKey = Boolean(previewParams) && areCloneVoucherPreviewParamsEqual(requestParams, previewParams);
    setPreviewParams(requestParams);
    if (samePreviewKey) {
      void previewQuery.refetch();
    }
  };

  const previewResult = previewQuery.data?.result;
  const showPreviewSummary = Boolean(previewParams && previewQuery.isSuccess && previewResult);
  const previewErrorMessage = previewQuery.isError ? formatClonePreviewErrorMessage(previewQuery.error) : '';
  const previewErrorLines = previewErrorMessage
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const previewErrorHeadline = previewErrorLines[0] ?? '';
  const previewErrorDetailBlock = previewErrorLines.slice(1).join('\n').trim();

  const handleClone = async () => {
    if (!canCloneAfterPreview) {
      const result = previewQuery.data?.result;
      if (
        previewMatchesCurrent &&
        previewCompleteForClone &&
        previewQuery.isSuccess &&
        result &&
        !result.newVoucherNameAvailable
      ) {
        enqueueSnackbar('This voucher name is not available. Choose a different name before cloning.', {
          variant: 'warning',
        });
      } else {
        enqueueSnackbar('Wait for a successful preview before cloning, or fix the errors shown above.', {
          variant: 'warning',
        });
      }
      return;
    }
    const body = buildCloneBody(
      newVoucherName,
      sourceVoucherInput,
      sourceVoucherId,
      region,
      amount,
      regionalCloneMode,
      caseId,
      reason,
      voucherUserRows,
    );
    if (!body) {
      enqueueSnackbar(validationMessageForMissingBaseFields(regionFilled, regionalCloneMode), { variant: 'warning' });
      return;
    }
    try {
      const res = await cloneMutation.mutateAsync(promoBrand ? { ...body, brand: promoBrand } : body);
      const newId = res?.result?.voucherId ?? '';
      if (!newId) {
        enqueueSnackbar('Clone succeeded but no voucher id was returned.', { variant: 'warning' });
        return;
      }
      enqueueSnackbar('Voucher cloned successfully', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      onCloned(newId);
      onClose();
    } catch (e) {
      const message = formatClonePreviewErrorMessage(e);
      enqueueSnackbar(`Clone failed: ${message}`, { variant: 'error' });
    }
  };

  const addUserRow = () => setVoucherUserRows((rows) => [...rows, newRow()]);
  const removeUserRow = (id: string) => setVoucherUserRows((rows) => rows.filter((r) => r.id !== id));
  const updateUserRow = (id: string, patch: Partial<VoucherUserRow>) => {
    setVoucherUserRows((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const handleAssignUsersToSourceVoucher = useCallback(async () => {
    const vid = targetVoucherIdForAssign;
    if (!vid || assignedUserIdsForSourceVoucher.length === 0) {
      enqueueSnackbar('Select at least one user.', { variant: 'warning' });
      return;
    }
    try {
      const response = await assignUsersMutation.mutateAsync({
        voucher_id: vid,
        expired_at: normalizeToISODate(
          dayjs().tz(SYDNEY_TIMEZONE).add(DEFAULT_VOUCHER_USER_EXPIRY_YEARS, 'year').format('YYYY-MM-DD'),
        ),
        voucher_users: assignedUserIdsForSourceVoucher.map((user_id) => ({ user_id })),
        ...(promoBrand ? { brand: promoBrand } : {}),
      });
      const r = response.result;
      const created = r?.createdVoucherUsers?.length ?? 0;
      const updated = r?.updatedVoucherUsers?.length ?? 0;
      const reactivated = r?.reactivatedVoucherUsers?.length ?? 0;
      const skipped = r?.skippedVoucherUsers?.length ?? 0;
      const ok = created + updated + reactivated;
      if (ok === 0 && skipped > 0) {
        enqueueSnackbar('No users were assigned (all skipped).', { variant: 'warning' });
      } else if (skipped > 0) {
        enqueueSnackbar(`Assigned ${ok} user(s); ${skipped} skipped.`, { variant: 'warning' });
      } else if (ok === 0 && skipped === 0) {
        enqueueSnackbar('Users assigned to this voucher.', { variant: 'success' });
      } else if (ok === 1) {
        enqueueSnackbar('User assigned to this voucher.', { variant: 'success' });
      } else {
        enqueueSnackbar(`${ok} users assigned to this voucher.`, { variant: 'success' });
      }
      queryClient.invalidateQueries({ queryKey: ['voucher-users', vid] });
      queryClient.invalidateQueries({ queryKey: ['voucher', vid] });
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      setVoucherUserRows([]);
      onClose();
    } catch (error) {
      enqueueSnackbar(`Failed to assign users: ${(error as Error).message}`, { variant: 'error' });
    }
  }, [
    assignUsersMutation,
    assignedUserIdsForSourceVoucher,
    enqueueSnackbar,
    onClose,
    queryClient,
    promoBrand,
    targetVoucherIdForAssign,
  ]);

  const sourceDisplayNameTrimmed = sourceVoucherDisplayName?.trim() ?? '';
  const launchedWithVoucherName = Boolean(sourceDisplayNameTrimmed);

  const sourceHint = useMemo(() => {
    const raw = sourceVoucherInput.trim();
    if (!raw) {
      if (launchedWithVoucherName) {
        return `Source is this voucher ("${sourceDisplayNameTrimmed}").`;
      }
      return `Source is this voucher (ID ${sourceVoucherId}).`;
    }
    if (SOURCE_VOUCHER_UUID_RE.test(raw)) {
      return `Source will be the voucher with ID ${raw}.`;
    }
    return `Source is "${raw}".`;
  }, [sourceVoucherInput, launchedWithVoucherName, sourceDisplayNameTrimmed, sourceVoucherId]);

  const dialogTitleId = 'voucher-clone-dialog-title';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby={dialogTitleId}>
      <DialogTitle id={dialogTitleId} sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
          Clone voucher
        </Box>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Tooltip title={moreInfo ? 'Hide detailed field help' : 'Show detailed field help'}>
            <ToggleButton
              value="info"
              size="small"
              selected={moreInfo}
              onChange={() => setMoreInfo((v) => !v)}
              aria-label={moreInfo ? 'Hide detailed field information' : 'Show detailed field information'}
            >
              <InfoOutlinedIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <IconButton aria-label="Close" edge="end" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {moreInfo && (
            <Typography variant="body2" color="text.secondary">
              {sourceHint}
            </Typography>
          )}
          <TextField
            label={isSourceLocked ? 'Source voucher' : 'Source voucher ID or name (optional)'}
            value={sourceVoucherInput}
            onChange={(e) => setSourceVoucherInput(e.target.value)}
            fullWidth
            disabled={isSourceLocked}
            placeholder={
              isSourceLocked ? undefined : 'Leave empty for this voucher, or enter another voucher UUID or name'
            }
            helperText={
              moreInfo
                ? isSourceLocked
                  ? 'The rewards and conditions of this voucher will be cloned.'
                  : 'Empty = this voucher. Otherwise enter a UUID or another voucher’s name.'
                : undefined
            }
          />
          <TextField
            label="New voucher name"
            value={newVoucherName}
            onChange={(e) => setNewVoucherName(e.target.value)}
            required
            fullWidth
            error={Boolean(newVoucherNameError)}
            helperText={
              newVoucherNameError ||
              (moreInfo ? `The name used for the new voucher. ${CLONE_VOUCHER_NAME_PATTERN_MESSAGE}.` : undefined)
            }
          />
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Internal reference
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                fullWidth
                helperText={
                  moreInfo
                    ? 'Internal note describing why this cloned voucher exists (not shown to customers).'
                    : 'Internal note (not shown to customers).'
                }
              />
              <TextField
                label="Case ID"
                value={caseId}
                onChange={(e) => setCaseId(e.target.value)}
                fullWidth
                helperText={
                  moreInfo
                    ? 'Internal CaseId for the cloned voucher. Stored uppercased.'
                    : 'Internal only; stored uppercased.'
                }
              />
            </Stack>
          </Box>

          <Accordion
            defaultExpanded={false}
            disableGutters
            elevation={0}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Regional clone (optional)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="voucher-clone-region-label" shrink>
                    Region (optional)
                  </InputLabel>
                  <Select
                    labelId="voucher-clone-region-label"
                    label="Region (optional)"
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    displayEmpty
                    renderValue={renderRegionSelectValue}
                  >
                    <MenuItem value="">
                      <Typography component="span" sx={{ fontStyle: 'italic' }}>
                        Clone all regions
                      </Typography>
                    </MenuItem>
                    {regionOptions.map((r) => {
                      if (!hasPerRegionRewardHints) {
                        return (
                          <MenuItem key={r.code} value={r.code}>
                            {r.name} ({r.code})
                          </MenuItem>
                        );
                      }
                      const summary = summarizeVoucherRewardsForRegion(
                        sourceVoucherRewards,
                        r.code,
                        validRegionCodes,
                        sourceVoucherCurrencyCode,
                      );
                      const noReward = summary.kind === 'none';
                      const presentation = regionRewardSelectPresentation(summary);
                      return (
                        <MenuItem
                          key={r.code}
                          value={r.code}
                          disabled={noReward}
                          sx={
                            noReward
                              ? (theme) => ({
                                  bgcolor: theme.palette.action.disabledBackground,
                                  opacity: 1,
                                  '&.Mui-disabled': { opacity: 1 },
                                })
                              : undefined
                          }
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'baseline',
                              gap: 0.5,
                              width: '100%',
                              overflow: 'hidden',
                            }}
                          >
                            <Typography
                              component="span"
                              variant="body2"
                              color={noReward ? 'text.disabled' : 'text.primary'}
                              sx={{ flexShrink: 0 }}
                            >
                              {r.name} ({r.code})
                            </Typography>
                            <Typography
                              component="span"
                              variant="body2"
                              color={noReward ? 'text.disabled' : 'text.secondary'}
                              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                              {presentation.suffix}
                            </Typography>
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {moreInfo && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {regionFilled
                        ? regionalCloneMode === 'regionOnly'
                          ? cannotRegionalAmountOverride
                            ? 'No amount: clones every reward for this region. Preview must succeed before Clone is enabled.'
                            : 'Copies discounts for the chosen region only, keeping source amounts. Preview must succeed before Clone is enabled.'
                          : 'Enter a reward amount (≥ 0). Preview must succeed before Clone is enabled.'
                        : 'Leave as all regions, or pick one region to clone a subset or override the reward amount.'}
                    </Typography>
                  )}
                </FormControl>
                {!regionFilled ? (
                  <Typography variant="body2" color="text.secondary">
                    Select a region to clone that region only, or override the reward amount for that region.
                  </Typography>
                ) : (
                  <>
                    {cannotRegionalAmountOverride && (
                      <Alert severity="info">
                        <Typography variant="body2">
                          This region has more than one reward row for its currency, so you cannot set one override
                          amount. With <strong>Region only</strong> (no amount), clone still works: promo copies every
                          reward for that region. To change amounts first, edit rewards or use{' '}
                          <strong>Clone all regions</strong>.
                        </Typography>
                      </Alert>
                    )}
                    <FormControl component="fieldset" variant="standard">
                      <Typography component="legend" variant="body2" sx={{ mb: 0.5 }}>
                        Regional clone mode
                      </Typography>
                      <RadioGroup
                        value={regionalCloneMode}
                        onChange={(e) => setRegionalCloneMode(e.target.value as RegionalVoucherCloneMode)}
                      >
                        <FormControlLabel
                          value="regionOnly"
                          control={<Radio size="small" />}
                          label={
                            <Box>
                              <Typography variant="body2">Region only — copy that region’s discounts</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {cannotRegionalAmountOverride
                                  ? 'No amount is sent; all reward rows for this region are cloned as-is.'
                                  : 'Keeps source reward amounts; does not override the amount.'}
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value="amountOverride"
                          control={<Radio size="small" />}
                          disabled={cannotRegionalAmountOverride}
                          label={
                            <Box>
                              <Typography
                                variant="body2"
                                color={cannotRegionalAmountOverride ? 'text.disabled' : 'text.primary'}
                              >
                                Set reward amount for this region
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {cannotRegionalAmountOverride
                                  ? 'Not available when this region has multiple reward amounts (same currency).'
                                  : 'Single regional reward with the amount you enter below.'}
                              </Typography>
                            </Box>
                          }
                        />
                      </RadioGroup>
                    </FormControl>
                    {regionalCloneMode === 'amountOverride' && (
                      <TextField
                        label="Reward amount"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        fullWidth
                        type="number"
                        required
                        inputProps={{ min: 0, step: 'any' }}
                        helperText={
                          moreInfo ? 'Required (≥ 0). Pre-filled when this voucher has one matching reward.' : undefined
                        }
                      />
                    )}
                  </>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion
            defaultExpanded={false}
            disableGutters
            elevation={0}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Assign users on clone (optional)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Box>
                  <Button size="small" startIcon={<AddIcon />} onClick={addUserRow} variant="text">
                    Add user
                  </Button>
                </Box>
                {voucherUserRows.length === 0 ? (
                  moreInfo ? (
                    <Typography variant="body2" color="text.secondary">
                      Add rows to assign to the voucher being cloned.
                    </Typography>
                  ) : null
                ) : (
                  <Stack spacing={2}>
                    {showRegionalCloneUserAdvice && (
                      <Alert severity="info">
                        <Typography variant="body2">
                          If these users should use this voucher as it is now, use <strong>Add voucher users</strong> on
                          the voucher page instead of cloning—cloning always creates a new voucher.
                        </Typography>
                      </Alert>
                    )}
                    {showAssignOnVoucherInsteadSuggestion && (
                      <Alert severity="info">
                        <Stack spacing={1.5}>
                          <Typography variant="body2">
                            You are assigning users while cloning the full voucher (all regions) without changing reward
                            amounts. If you only need to link users to this voucher, you can assign them to the source
                            voucher below, or cancel and use <strong>Add voucher users</strong> on the voucher
                            page—cloning always creates a new voucher with a new name.
                          </Typography>
                          {targetVoucherIdForAssign ? (
                            <Box>
                              <LoadingButton
                                variant="outlined"
                                color="primary"
                                size="small"
                                loading={assignUsersMutation.isPending}
                                disabled={!canAssignUsersToSourceVoucher || cloneMutation.isPending}
                                onClick={handleAssignUsersToSourceVoucher}
                              >
                                {assignedUserIdsForSourceVoucher.length === 1
                                  ? 'Assign user to this voucher'
                                  : `Assign ${assignedUserIdsForSourceVoucher.length} users to this voucher`}
                              </LoadingButton>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {`Uses the same default expiry as Add voucher users (${DEFAULT_VOUCHER_USER_EXPIRY_YEARS} ${
                                  DEFAULT_VOUCHER_USER_EXPIRY_YEARS === 1 ? 'year' : 'years'
                                } from today).`}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Enter the source voucher as a UUID in the source field, or go back to this voucher’s edit
                              page to assign users—the source voucher must be known to assign from here.
                            </Typography>
                          )}
                        </Stack>
                      </Alert>
                    )}
                    {voucherUserRows.map((row) => (
                      <Stack
                        key={row.id}
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ sm: 'flex-start' }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <UserSearchWidget
                            id={`voucher-clone-user-${row.id}`}
                            label="Name / Email / Customer Support Code / User ID"
                            formErrors={[]}
                            onChange={(_, value) =>
                              updateUserRow(row.id, {
                                userId: value,
                                ...(value === '' ? { userSearchLabel: '' } : {}),
                              })
                            }
                            passedAutocompleteValue={row.userSearchLabel}
                            setPassedAutocompleteValue={(next) => {
                              setVoucherUserRows((rows) =>
                                rows.map((r) => {
                                  if (r.id !== row.id) {
                                    return r;
                                  }
                                  const prev = r.userSearchLabel;
                                  const label = typeof next === 'function' ? next(prev) : next;
                                  return { ...r, userSearchLabel: label };
                                }),
                              );
                            }}
                          />
                        </Box>
                        <IconButton aria-label="Remove user row" onClick={() => removeUserRow(row.id)} size="small">
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            {previewQuery.isFetching && (
              <Typography variant="caption" color="text.secondary">
                Loading preview…
              </Typography>
            )}
            {canTriggerPreview && !canCloneAfterPreview && !previewQuery.isFetching && (
              <Typography variant="caption" color="text.secondary" sx={{ maxWidth: '100%' }}>
                {previewQuery.isError ? (
                  <>
                    <Box component="span" sx={{ color: 'error.main', display: 'block', fontWeight: 500 }}>
                      {previewErrorHeadline}
                    </Box>
                    <Box component="span" sx={{ display: 'block', mt: 0.25 }}>
                      Fix preview errors to enable Clone.
                    </Box>
                  </>
                ) : regionFilled && !amountValid ? (
                  'Enter a reward amount (0 or greater) to finish preview.'
                ) : previewQuery.isSuccess &&
                  previewQuery.data?.result &&
                  !previewQuery.data.result.newVoucherNameAvailable &&
                  previewMatchesCurrent &&
                  previewCompleteForClone ? (
                  'This voucher name is not available. Choose a different name before cloning.'
                ) : (
                  'Waiting for preview…'
                )}
              </Typography>
            )}
          </Stack>

          {previewQuery.isError && previewErrorDetailBlock && (
            <Alert severity="error">
              <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                {previewErrorDetailBlock}
              </Typography>
            </Alert>
          )}

          {showPreviewSummary && previewResult && (
            <Alert severity={previewResult.newVoucherNameAvailable ? 'success' : 'warning'}>
              <Typography variant="body2">
                Mode: {previewResult.cloneMode} · Rewards: {previewResult.rewardCount} · Conditions:{' '}
                {previewResult.conditionCount}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                New name available: {previewResult.newVoucherNameAvailable ? 'yes' : 'no'}
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handlePreview} disabled={!canTriggerPreview} aria-label="Refresh preview">
          <RefreshIcon />
        </Button>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={cloneMutation.isPending}
          onClick={handleClone}
          disabled={!canCloneAfterPreview}
        >
          Clone
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
